import type {
  BlockSubscriptionSettingsStoryblok,
  NewsletterConfigStoryblok,
  NewsletterContentStoryblok,
} from '~/types/storyblok'

export const useNewsletterContent = (
  blok?:
    | BlockSubscriptionSettingsStoryblok
    | NewsletterConfigStoryblok
    | NewsletterContentStoryblok,
  contentType?: string,
) => {
  const { subscriptionConfig } = storeToRefs(useConfigStore())

  const specialTitle = computed(() => {
    switch (contentType) {
      case 'News':
        return subscriptionConfig.value?.newsTitle
          ? subscriptionConfig.value?.newsTitle
          : null
      case 'Events':
        return subscriptionConfig.value?.eventsTitle
          ? subscriptionConfig.value?.eventsTitle
          : null
      case 'Resource':
        return subscriptionConfig.value?.resourcesTitle
          ? subscriptionConfig.value?.resourcesTitle
          : null
      case 'RealStories':
        return subscriptionConfig.value?.realStoriesTitle
          ? subscriptionConfig.value?.realStoriesTitle
          : null
      default:
        return null
    }
  })

  // Popup Enabled?
  const isPopupDisabled = computed(() => subscriptionConfig.value?.disablePopup)

  // Toggle
  const isToggleEnabled = computed(
    () => subscriptionConfig.value?.showToggleButton,
  )

  // Label
  const subscribeButtonLabel = computed(
    () => subscriptionConfig.value?.subscribeButtonLabel,
  )

  // Title
  const globalTitle = computed(
    () => subscriptionConfig.value?.content?.[0]?.title,
  )
  const title = computed(
    () =>
      blok?.title ||
      blok?.content?.[0]?.title ||
      specialTitle.value ||
      globalTitle.value ||
      'Subscribe',
  )

  // Description
  const globalDescription = computed(
    () => subscriptionConfig.value?.content?.[0]?.description,
  )
  const descriptionOverrides = computed(
    () => blok?.description || blok?.content?.[0]?.description,
  )
  const description = computed(
    () => descriptionOverrides.value || globalDescription.value,
  )
  const popupShowDescription = computed(
    () =>
      !!descriptionOverrides.value ||
      subscriptionConfig.value?.showPopupDescription,
  )

  // Benefits
  const globalBenefits = computed(
    () => subscriptionConfig.value?.content?.[0]?.benefits,
  )
  const benefitsOverrides = computed(
    () => blok?.benefits || blok?.content?.[0]?.benefits,
  )
  const benefits = computed(
    () => benefitsOverrides.value || globalBenefits.value,
  )
  const popupShowBenefits = computed(
    () =>
      !!benefitsOverrides.value || subscriptionConfig.value?.showPopupBenefits,
  )

  // Learn more
  const learnMoreLink = computed(() => subscriptionConfig.value?.link)

  // Thank you
  const thankYouMessage = computed(
    () => subscriptionConfig.value?.thankYouMessage,
  )

  // Error
  const errorMessage = computed(() => subscriptionConfig.value?.errorMessage)

  // Popup Style
  const popupStyle = computed(() => {
    let sizedImageUrl: string | null = null
    const image = subscriptionConfig.value?.backgroundImage

    // get a sensibly sized image
    if (image?.filename) {
      sizedImageUrl = `${image.filename.toString()}/m/1260x0/smart/`
    }

    // set bg colour
    const fill =
      subscriptionConfig.value?.backgroundColor?.color !== '#f40000'
        ? subscriptionConfig.value?.backgroundColor?.color
        : undefined

    return {
      backgroundImage: sizedImageUrl ? `url(${sizedImageUrl})` : undefined,
      backgroundColor: fill,
    }
  })

  // Download Blocker
  const isBlockerDisabled = computed(() =>
    blok?.disabled ? true : !!subscriptionConfig.value?.disableContentBlock,
  )
  const downloadsTitle = computed(
    () => subscriptionConfig.value?.downloadsTitle,
  )
  const downloadsDescription = computed(
    () => subscriptionConfig.value?.downloadsDescription,
  )
  const downloadsSkipLabel = computed(
    () => subscriptionConfig.value?.downloadsSkipLabel,
  )

  return {
    contentType,
    isPopupDisabled,
    isToggleEnabled,
    subscribeButtonLabel,
    title,
    description,
    benefits,
    learnMoreLink,
    thankYouMessage,
    errorMessage,
    popupShowDescription,
    popupShowBenefits,
    popupStyle,
    isBlockerDisabled,
    downloadsTitle,
    downloadsDescription,
    downloadsSkipLabel,
  }
}
